import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { IntegrationCard } from './Card/IntegrationCard';
import USCConnectLogo from '../../Assets/Icons/3rdParty/uscconnect.svg';
import EversportsLogo from '../../Assets/Icons/3rdParty/eversports.svg';
import MagicLineLogo from '../../Assets/Icons/3rdParty/magicline-logo.webp';
import VirtuaGymLogo from '../../Assets/Icons/3rdParty/vg_logo.png';
import ZingfitLogo from '../../Assets/Icons/3rdParty/zf_logo.png';
import MindbodyLogo from '../../Assets/Icons/3rdParty/mb_logo.png';
import ResamaniaLogo from '../../Assets/Icons/3rdParty/xr_logo.png';
import { useProvider } from '~/Hooks';
import { useRequestActiveIntegrations } from '~/requests/userService/useRequestIntegrations/useRequestActiveIntegrations';
import { JumpingLoader } from '~/UIKit';
import { useRequestChannels } from '~/requests/adapterSvc/useRequestChannels';
import { IIntegrationChannel } from '~/Models/DTO/IntegrationChannels.model';
import { IntegrationFilterInput } from '~/Components';
import { Typography } from '@mui/material';

export const Integrations = () => {
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const providerUUID = String(currentProvider?.uuid);
  const [searchQuery, setSearchQuery] = useState('');

  const [magiclineIntegrations, setMagiclineIntegrations] = useState<IIntegrationChannel[]>([]);
  const [eversportsIntegrations, setEversportsIntegrations] = useState<IIntegrationChannel[]>([]);
  const [virtuagymIntegration, setVirtuagymIntegration] = useState<IIntegrationChannel[]>([]);
  const [zingfitIntegration, setZingfitIntegration] = useState<IIntegrationChannel[]>([]);
  const [mindbodyIntegration, setMindbodyIntegration] = useState<IIntegrationChannel[]>([]);
  const [virtuagymV1Integration, setVirtuagymV1Integration] = useState<IIntegrationChannel[]>([]);
  const [resamaniaIntegration, setResamaniaIntegration] = useState<IIntegrationChannel[]>([]);

  const {
    data: USCConnectIntegrations,
    isLoading: USCConnectIntegrationsLoading,
  } = useRequestActiveIntegrations({ prodOnly: 'false', providerId: providerUUID });

  const { data: integrations, isLoading: integrationsLoading } = useRequestChannels({
    providerId: providerUUID,
  });

  const separateIntegrations = () => {
    const magicline = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'magicline',
    );
    if (magicline) setMagiclineIntegrations(magicline);

    const eversports = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'eversports',
    );
    if (eversports) setEversportsIntegrations(eversports);

    const virtuagym = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'virtuagym',
    );
    if (virtuagym) setVirtuagymIntegration(virtuagym);

    const zingfit = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'zingfit',
    );
    if (zingfit) setZingfitIntegration(zingfit);

    const mindbody = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'mindbody',
    );
    if (mindbody) setMindbodyIntegration(mindbody);

    const virtuagymV1 = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'virtuagymv1',
    );
    if (virtuagymV1) setVirtuagymV1Integration(virtuagymV1);

    const resamania = integrations?.data?.filter(
      (obj: IIntegrationChannel) => obj.integration === 'resamania',
    );
    if (resamania) setResamaniaIntegration(resamania);
  };

  useEffect(() => {
    separateIntegrations();
  }, [integrations?.data?.length]);

  const filteredIntegrations = useMemo(() => {
    const allIntegrations = [
      {
        title: t('eversports.title'),
        integrations: eversportsIntegrations,
        logo: EversportsLogo,
        description: t('integrations.activate.usc.connect.description'),
        link: 'eversports',
      },
      {
        title: t('integrations.activate.usc.connect'),
        integrations: USCConnectIntegrations?.[0]?.clientId ? [USCConnectIntegrations[0]] : [],
        logo: USCConnectLogo,
        description: t('integrations.activate.usc.connect.description'),
        link: 'usc',
      },
      {
        title: t('magicline.title'),
        integrations: magiclineIntegrations,
        logo: MagicLineLogo,
        description: t('integrations.activate.magicline'),
        link: 'magicline',
      },
      {
        title: t('virtuagymV1.title'),
        integrations: virtuagymV1Integration,
        logo: VirtuaGymLogo,
        description: t('integrations.activate.virtuagymV1.description'),
        link: 'virtuagymv1',
      },
      {
        title: t('virtuagym.title'),
        integrations: virtuagymIntegration,
        logo: VirtuaGymLogo,
        description: t('integrations.activate.virtuagym.description'),
        link: 'virtuagym',
      },
      {
        title: t('zingfit.title'),
        integrations: zingfitIntegration,
        logo: ZingfitLogo,
        description: t('integrations.activate.zingfit.description'),
        link: 'zingfit',
      },
      {
        title: t('integrations.activate.mindbody.title'),
        integrations: mindbodyIntegration,
        logo: MindbodyLogo,
        description: t('integrations.activate.mindbody.description'),
        link: 'mindbody',
      },
      {
        title: t('resamania.title'),
        integrations: resamaniaIntegration,
        logo: ResamaniaLogo,
        description: t('integrations.activate.resamania.description'),
        link: 'resamania',
      },
    ];

    if (!searchQuery.trim()) return allIntegrations;

    const query = searchQuery.toLowerCase();
    return allIntegrations.filter(
      (integration) =>
        integration.title.toLowerCase().includes(query) ||
        integration.description.toLowerCase().includes(query),
    );
  }, [
    searchQuery,
    eversportsIntegrations,
    USCConnectIntegrations,
    magiclineIntegrations,
    virtuagymV1Integration,
    virtuagymIntegration,
    zingfitIntegration,
    mindbodyIntegration,
    resamaniaIntegration,
    t,
  ]);

  const integrationsCount = filteredIntegrations.length;

  if (USCConnectIntegrationsLoading || integrationsLoading) {
    return <JumpingLoader />;
  }

  return (
    <PrivateLayout title={`${t('menu.integrations')}: ${integrationsCount}`} hideBackButton>
      <IntegrationFilterInput
        placeholder={t('integrations.search')}
        onChange={(e) => setSearchQuery(e.target.value)}
        value={searchQuery}
        autoFocus
      />

      {filteredIntegrations.length === 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            width: '100%',
          }}
        >
          <Typography variant="h6">{t('no.integrations.found')}</Typography>
        </div>
      ) : (
        filteredIntegrations.map((integration) => (
          <IntegrationCard
            key={integration.link}
            title={integration.title}
            buttonText={
              integration.integrations && integration.integrations.length !== 0
                ? t('manage.integrations')
                : t('add.integration')
            }
            logo={integration.logo}
            description={integration.description}
            link={integration.link}
          />
        ))
      )}
    </PrivateLayout>
  );
};
